import React from 'react';
import { AlertCircle, RotateCcw } from 'lucide-react';
import OptionButtons from '@/components/OptionButtons';
import SuggestedActionButton from '@/components/SuggestedActionButton';
import MessageFeedback from '@/features/messages/components/MessageFeedback';
import InsightDisplay from './InsightDisplay';
import { 
  buildContextFromEncounterOption, 
  buildContextFromState, 
  buildStateFromEncounterOption 
} from '../utils/contextData';

/**
 * Component for rendering individual messages in the conversation
 * Handles all conditional rendering based on message type and content
 */
const HIDDEN_INTERNAL_MESSAGES = ['TEMPLATE_SELECTED', '__guided_skip__'];

function MessageItem({
  msg,
  index,
  messages,
  isSelectionMode,
  selectedMessages,
  setSelectedMessages,
  captureSelection,
  renderMessageWithViewNoteLinks,
  sendMessage,
  currentPatient,
  currentEncounter,
  setCurrentPatient,
  setCurrentEncounter,
  fetchNoteData,
  handleSelectPatient,
  guidedMode,
  onRetry
}) {
  if (msg.sender === 'user' && HIDDEN_INTERNAL_MESSAGES.includes(msg.text)) {
    return null;
  }

  return (
    <div className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'} px-4`}>
      <div
        data-testid={msg.sender === 'assistant' ? 'assistant-message' : undefined}
        className={`relative px-4 py-3 rounded-2xl max-w-[80%] md:max-w-[70%] whitespace-pre-wrap break-words
          ${msg.sender === 'user' ? 'bg-blue-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'}
          ${isSelectionMode && selectedMessages.includes(index) ? 'ring-2 ring-green-500 dark:ring-green-400' : ''}
          ${isSelectionMode ? 'cursor-pointer' : ''}
        `}
        onMouseUp={captureSelection}
        onClick={isSelectionMode ? () => {
          setSelectedMessages(prev =>
            prev.includes(index)
              ? prev.filter(idx => idx !== index)
              : [...prev, index]
          );
        } : undefined}
      >
        {isSelectionMode && (
          <div className="flex items-center gap-2 mb-1">
            <input
              type="checkbox"
              checked={selectedMessages.includes(index)}
              onChange={(e) => {
                e.stopPropagation();
                setSelectedMessages(prev =>
                  prev.includes(index)
                    ? prev.filter(idx => idx !== index)
                    : [...prev, index]
                );
              }}
              onClick={(e) => e.stopPropagation()}
              className="form-checkbox rounded text-blue-500 w-4 h-4"
            />
            <span className="text-xs opacity-70">Select this message</span>
          </div>
        )}
        {msg.text && typeof msg.text === 'object' ? (
          <pre>{JSON.stringify(msg.text, null, 2)}</pre>
        ) : (
          renderMessageWithViewNoteLinks(msg.text)
        )}
        
        {msg.sender === 'assistant' && (msg.insights || msg.recommendations) && (
          <div className="mt-2">
            <InsightDisplay 
              insights={Array.isArray(msg.insights) ? msg.insights : []} 
              recommendations={Array.isArray(msg.recommendations) ? msg.recommendations : []} 
            />
          </div>
        )}
        {msg.sender === 'assistant' && Array.isArray(msg.options) && msg.options.length > 0 && (
          <div className="mt-2">
            <OptionButtons options={msg.options} sendMessage={sendMessage} />
          </div>
        )}
        {msg.sender === 'assistant' && msg.suggested_action && !msg.template_options && (
          <div className="mt-2">
            <SuggestedActionButton lastMessage={msg} sendMessage={sendMessage} />
          </div>
        )}
        
        {!guidedMode && msg.sender === 'assistant' && msg.encounter_options && msg.encounter_options.length > 0 && (() => {
          const lastIndexWithOptions = messages.map((m, idx) => 
            m.sender === 'assistant' && m.encounter_options && m.encounter_options.length > 0 ? idx : -1
          ).reduce((last, current) => current > last ? current : last, -1);
          return index === lastIndexWithOptions;
        })() && (
          <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600">
            <select
              className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm leading-tight"
              onChange={(e) => {
                const selectedValue = e.target.value;
                if (selectedValue === 'new_patient') {
                  window.dispatchEvent(new CustomEvent('open_patient_form'));
                  return;
                } else if (selectedValue === 'new_encounter') {
                  if (msg.patient_id) {
                    handleSelectPatient(msg.patient_id);
                  } else {
                    sendMessage('Create new encounter - select patient');
                  }
                } else if (selectedValue === 'other_session') {
                  const otherOption = msg.encounter_options.find(enc => enc.encounter_id === 'other_session');
                  sendMessage(otherOption?.label || 'Other session - view full list');
                  return;
                } else if (selectedValue === 'no_encounter_patient') {
                  sendMessage('Write note without encounter - which patient?');
                } else if (selectedValue === 'no_encounter' || selectedValue === 'misc_note') {
                  sendMessage('Write misc note - SOAP or General format?');
                } else if (selectedValue) {
                                  const selectedEncounter = msg.encounter_options.find(enc => enc.encounter_id === selectedValue);
                                  if (selectedEncounter) {
                                    // Update the currentEncounter and currentPatient state
                                    // so that subsequent template selection has access to these values
                                    const { encounter, patient } = buildStateFromEncounterOption(selectedEncounter);
                                    if (setCurrentEncounter && encounter) {
                                      setCurrentEncounter(encounter);
                                    }
                                    if (setCurrentPatient && patient) {
                                      setCurrentPatient(patient);
                                    }
                                    const contextData = buildContextFromEncounterOption(selectedEncounter);
                                    const label = selectedEncounter.label || '';
                                    const name = selectedEncounter.patient_name || 'this session';
                                    const msgText = label ? `Write note for ${name} — ${label}` : `Write note for ${name}`;
                                    sendMessage(msgText, null, contextData);
                                  }
                                }
              }}
              defaultValue=""
            >
              <option value="" disabled>Select a session...</option>
              {msg.encounter_options.map((opt) => (
                <option key={opt.encounter_id} value={opt.encounter_id} className="py-2">
                  {opt.patient_name && !opt.label?.includes(opt.patient_name)
                    ? `${opt.patient_name} — ${opt.label}`
                    : opt.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {msg.sender === 'assistant' && msg.note_type_options && msg.note_type_options.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600">
            {msg.note_type_options.filter(opt => opt.category === 'clinical_chart').length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
                  Select Clinical Note Type:
                </div>
                <div className="space-y-2">
                  {msg.note_type_options.filter(opt => opt.category === 'clinical_chart').map((opt) => (
                    <label
                      key={opt.value}
                      className="flex items-start gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-2 rounded"
                    >
                      <input
                        type="radio"
                        name={`clinical_note_type_${index}`}
                        value={opt.value}
                        defaultChecked={msg.note_type_options.filter(opt => opt.category === 'clinical_chart').length === 1}
                        className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm text-gray-900 dark:text-white">{opt.label}</div>
                        {opt.description && (
                          <div className="text-xs text-gray-600 dark:text-gray-400">{opt.description}</div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {msg.note_type_options.filter(opt => opt.category === 'add_on').length > 0 && (
              <div>
                <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
                  Optional Add-ons (check any):
                </div>
                <div className="space-y-2">
                  {msg.note_type_options.filter(opt => opt.category === 'add_on').map((opt) => (
                    <label
                      key={opt.value}
                      className="flex items-start gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-2 rounded"
                    >
                      <input
                        type="checkbox"
                        value={opt.value}
                        className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm text-gray-900 dark:text-white">{opt.label}</div>
                        {opt.description && (
                          <div className="text-xs text-gray-600 dark:text-gray-400">{opt.description}</div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <button
              className="mt-4 w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              onClick={(e) => {
                const container = (e.target as HTMLElement).closest('.mt-3');
                
                const selectedRadio = container.querySelector('input[type="radio"]:checked');
                if (!selectedRadio) {
                  return;
                }

                const selectedCheckboxes = container.querySelectorAll('input[type="checkbox"]:checked');
                
                const notePrefs = {};
                const noteLabels = [];

                const clinicalValue = (selectedRadio as HTMLInputElement).value;
                if (clinicalValue.startsWith('clinical_note:')) {
                  const subtype = clinicalValue.split(':')[1];
                  (notePrefs as Record<string, unknown>).clinical_note = true;
                  (notePrefs as Record<string, unknown>).clinical_subtype = subtype;
                  noteLabels.push(selectedRadio.closest('label').querySelector('.font-medium').textContent);
                }

                selectedCheckboxes.forEach(cb => {
                  notePrefs[(cb as HTMLInputElement).value] = true;
                  noteLabels.push(cb.closest('label').querySelector('.font-medium').textContent);
                });
                
                const contextData = buildContextFromState(currentEncounter, currentPatient);
                
                const hasClinicalNote = clinicalValue && clinicalValue.startsWith('clinical_note:');
                if (hasClinicalNote && !(contextData as Record<string, unknown>).encounter_id && !(contextData as Record<string, unknown>).patient_id) {
                  import('react-toastify').then(({ toast }) => {
                    toast.error('Please select a patient or encounter before generating clinical notes');
                  });
                  return;
                }
                
                sendMessage(`Generate ${noteLabels.join(', ')}`, notePrefs, contextData);
              }}
            >
              Continue
            </button>
          </div>
        )}

        {msg.sender === 'assistant' && msg.template_options && msg.template_options.length > 0 && (() => {
          // Separate clinical templates (dropdown) from add-on templates (checkboxes)
          const clinicalTemplates = msg.template_options.filter(opt => opt.visibility === 'clinical_chart' || !opt.visibility);
          const addonTemplates = msg.template_options.filter(opt => opt.visibility && opt.visibility !== 'clinical_chart');
          
          const getTemplateId = (opt) => {
            return opt.template_id || (typeof opt.value === 'string' && opt.value.startsWith('template:') 
              ? opt.value.slice('template:'.length) 
              : opt.value);
          };
          
          const getAddonKey = (opt) => {
            const isClientSummary = opt.label?.toLowerCase().includes('client summary') || opt.visibility === 'client_facing';
            const isProviderReflection = opt.label?.toLowerCase().includes('therapist reflection') || opt.label?.toLowerCase().includes('provider reflection') || opt.visibility === 'provider_only';
            return isClientSummary ? 'client_summary' : (isProviderReflection ? 'provider_reflection' : null);
          };

          // Resolve the default template: prefer explicit ID, fall back to name match
          let resolvedDefaultTemplateId = msg.default_template_id || "";
          if (!resolvedDefaultTemplateId && msg.default_template_name) {
            const match = clinicalTemplates.find(
              opt => opt.label?.toLowerCase() === msg.default_template_name.toLowerCase()
            );
            if (match) {
              resolvedDefaultTemplateId = getTemplateId(match);
            }
          }
          
          return (
            <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600 template-selector-container">
              {/* Clinical templates as dropdown */}
              {clinicalTemplates.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
                    Select Clinical Note Template:
                  </div>
                  <select
                    className="template-dropdown w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    defaultValue={resolvedDefaultTemplateId}
                    onChange={(e) => {
                      // Show description of selected template
                      const descEl = e.target.closest('.mb-4').querySelector('.template-description');
                      const selectedOpt = clinicalTemplates.find(opt => getTemplateId(opt) === e.target.value);
                      if (descEl && selectedOpt?.description) {
                        descEl.textContent = selectedOpt.description;
                        descEl.classList.remove('hidden');
                      } else if (descEl) {
                        descEl.classList.add('hidden');
                      }
                    }}
                  >
                    <option value="" disabled>Select a template...</option>
                    {clinicalTemplates.map((opt) => {
                      const templateId = getTemplateId(opt);
                      return (
                        <option key={opt.value} value={templateId}>
                          {opt.label}
                        </option>
                      );
                    })}
                  </select>
                  <div className="template-description hidden mt-2 text-xs text-gray-600 dark:text-gray-400 italic"></div>
                </div>
              )}

              {/* Add-on templates (TR/CS) as checkboxes */}
              {addonTemplates.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
                    Optional Add-ons:
                  </div>
                  <div className="space-y-2">
                    {addonTemplates.map((opt) => {
                      const templateId = getTemplateId(opt);
                      const addonKey = getAddonKey(opt);
                      return (
                        <label
                          key={opt.value}
                          className="flex items-start gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-2 rounded"
                        >
                          <input
                            type="checkbox"
                            value={templateId}
                            data-addon-key={addonKey}
                            data-template-name={opt.label}
                            className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-900 dark:text-white">{opt.label}</div>
                            {opt.description && (
                              <div className="text-xs text-gray-600 dark:text-gray-400">{opt.description}</div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <button
                className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                onClick={(e) => {
                  const container = (e.target as HTMLElement).closest('.template-selector-container');
                  const dropdown = container.querySelector('.template-dropdown');
                  
                  if (!dropdown || !(dropdown as HTMLSelectElement).value) {
                    import('react-toastify').then(({ toast }) => {
                      toast.warning('Please select a note template');
                    });
                    return;
                  }

                  const templateId = (dropdown as HTMLSelectElement).value;
                  
                  // Collect add-on selections from checkboxes
                  const selectedCheckboxes = container.querySelectorAll('input[type="checkbox"]:checked');
                  const selections = {};
                  selectedCheckboxes.forEach(cb => {
                    const addonKey = cb.getAttribute('data-addon-key');
                    if (addonKey) {
                      selections[addonKey] = true;
                    }
                  });
                  
                  // Build ai_context with template_id, add_ons, AND encounter/patient context
                  // This ensures the backend state machine stays in the correct state
                  const contextData = buildContextFromState(
                    currentEncounter, 
                    currentPatient, 
                    { template_id: templateId, selections }
                  );
                  
                  sendMessage("TEMPLATE_SELECTED", null, contextData);
                }}
              >
                Continue
              </button>
            </div>
          );
        })()}
        
        {msg.sender === 'assistant' &&
          msg.note_data &&
          msg.note_data.note_id &&
          !(typeof msg.text === 'string' && /<view_note\b/i.test(msg.text)) && (
            <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600">
              <button
                className="w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
                onClick={() => {
                  if (typeof fetchNoteData === 'function') {
                    fetchNoteData(msg.note_data.note_id);
                  }
                }}
              >
                View Note
              </button>
            </div>
          )}
        
        {/* Failed message indicator + retry button */}
        {msg.sender === 'user' && msg.status === 'failed' && (
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-red-300/50">
            <AlertCircle size={14} className="text-red-300 flex-shrink-0" />
            <span className="text-xs text-red-200">Failed to send</span>
            <button
              className="flex items-center gap-1 text-xs text-white bg-red-600/80 hover:bg-red-600 px-2 py-1 rounded-md transition-colors min-h-[28px]"
              onClick={(e) => {
                e.stopPropagation();
                if (onRetry) {
                  onRetry(msg.text, msg._notePrefsOverride || null, msg._overrideContext || null, msg.timestamp);
                }
              }}
            >
              <RotateCcw size={12} />
              Retry
            </button>
          </div>
        )}

        {msg.sender === 'assistant' && msg.errorType === 'save_failed' && (() => {
          const previousUserMessage = [...messages.slice(0, index)]
            .reverse()
            .find((message) => message.sender === 'user' && message.text);

          return (
            <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-amber-300/50">
              <AlertCircle size={14} className="text-amber-600 dark:text-amber-300 flex-shrink-0" />
              <span className="text-xs text-amber-800 dark:text-amber-200">The note content is still here.</span>
              {previousUserMessage && (
                <button
                  className="flex items-center gap-1 text-xs text-white bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded-md transition-colors min-h-[32px]"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRetry?.(
                      previousUserMessage.text,
                      previousUserMessage._notePrefsOverride || null,
                      previousUserMessage._overrideContext || null,
                      previousUserMessage.timestamp,
                    );
                  }}
                >
                  <RotateCcw size={12} />
                  Try again
                </button>
              )}
              <button
                className="text-xs text-amber-800 dark:text-amber-200 border border-amber-400/70 hover:bg-amber-100 dark:hover:bg-amber-900/30 px-2 py-1 rounded-md transition-colors min-h-[32px]"
                onClick={(e) => {
                  e.stopPropagation();
                  sendMessage('__guided_skip__');
                }}
              >
                Continue to next session
              </button>
            </div>
          );
        })()}

      </div>
      {msg.sender === 'assistant' && (
        <div className="relative -ml-1 -mt-1 self-end">
          <MessageFeedback messageId={index} />
        </div>
      )}
    </div>
  );
}

export default MessageItem;
